import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import type { NotificationTemplate } from '../../models';
import { useGetNotificationTemplatesQuery } from '../../services/connectApi';

interface ConnectTemplateAutocompleteProps {
  value: NotificationTemplate | null;
  onChange: (value: NotificationTemplate | null) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

const ConnectTemplateAutocomplete: React.FC<ConnectTemplateAutocompleteProps> = ({ 
  value, 
  onChange, 
  label = 'Template', 
  placeholder = 'Search templates by name or description',
  error,
  helperText
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Setup debounce for search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300);
    
    return () => clearTimeout(handler);
  }, [inputValue]);

  // Get templates from the API with search
  const { data, isLoading } = useGetNotificationTemplatesQuery({ 
    page: 1, 
    page_size: 10, 
    search: searchTerm 
  }, {
    skip: !searchTerm
  });
  
  const templates = data?.results || [];

  return (
    <Autocomplete
      options={templates}
      value={value}
      onChange={(_e, newValue) => onChange(newValue)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterSelectedOptions
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ConnectTemplateAutocomplete;
